import { useStore } from '@nanostores/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TokenUsageHeader } from './Header';
import { TokenUsageMembersTableContent } from './MembersTableContent';
import { SectionContainer } from '~/components/dialogs/settings/SectionContainer';
import { Pagination } from '~/components/ui/Pagination';
import { Table, TableHead } from '~/components/ui/Table';
import { TeamPlanPricingSettingsSection } from '~/components/ui/pricing/SettingsSection/TeamPlan';
import { usePaginatedData } from '~/lib/hooks/usePaginatedData';
import { chatStore } from '~/lib/stores/chat';
import { organizationMembersStore } from '~/lib/stores/organizationMembers';
import { redirectToOrgBillingPage } from '~/lib/stores/pricing/helpers';
import type { OrganizationData } from '~/types/organization';
import type { EffectiveTier } from '~/utils/effectiveTier';
import { withSpinner } from '~/utils/withSpinner';

interface Props {
  closeDialog: VoidFunction;
  activeOrganization: OrganizationData;
  tier?: EffectiveTier;
}

const PAGE_SIZE = 5;

export const AdminTokenUsage = ({ closeDialog, activeOrganization, tier }: Props) => {
  const tokenStats = useStore(chatStore.tokenStats);
  const allOrgMembers = useStore(organizationMembersStore.activeOrgMembers);
  const isLoading = useStore(organizationMembersStore.isMembersLoading);
  const orgMembers = useMemo(() => allOrgMembers.filter((member) => !member.user.pending), [allOrgMembers]);

  const [isRedirecting, setIsRedirecting] = useState(false);

  const {
    data: paginatedMembers,
    page,
    onPageChange,
    totalPages,
  } = usePaginatedData(orgMembers, {
    pageSize: PAGE_SIZE,
  });

  const subscription = activeOrganization?.billing?.subscription;
  const hasActiveSubscription = subscription?.status === 'active';
  const nextMonthStartTimestamp = subscription?.nextMonthStartTimestamp;
  const quantity = subscription?.quantity;

  const hasTokenRefillData = hasActiveSubscription && tokenStats && nextMonthStartTimestamp && quantity;

  useEffect(() => {
    organizationMembersStore.fetchMembers(activeOrganization);
  }, [activeOrganization?.id]);

  useEffect(() => {
    if (paginatedMembers.length === 0) {
      return;
    }

    const memberProviderIds = paginatedMembers.map((m) => m.user.providerId);

    organizationMembersStore.fetchMembersTokens(
      activeOrganization,
      memberProviderIds,
      tier || {
        plan: 'teams',
        level: 0,
        isPaid: false,
      },
    );
  }, [paginatedMembers]);

  const gridColumnClassName = 'grid grid-cols-2 items-center';

  const onManageBillingClick = useCallback(() => {
    withSpinner({
      action: async () => {
        await redirectToOrgBillingPage(activeOrganization);
      },
      onStateUpdate: setIsRedirecting,
    });
  }, [activeOrganization]);

  return (
    <SectionContainer
      title={
        <TokenUsageHeader
          tokens={hasTokenRefillData ? tokenStats?.maxPerMonth * quantity : undefined}
          billingDate={hasTokenRefillData ? nextMonthStartTimestamp : undefined}
          canManageBilling={!!activeOrganization.billing}
          onManageBilling={onManageBillingClick}
          isLoading={isRedirecting}
          isTeam
        />
      }
      className="gap-8"
    >
      <div className="flex items-center gap-3 max-w-full">
        <div className="flex items-center justify-center size-12 rounded-full shrink-0 bg-bolt-ds-surfaceTwo border border-bolt-ds-inverseOnSurface">
          <div className="i-heroicons:building-storefront text-bolt-ds-iconSecondary size-6" />
        </div>

        <div className="flex flex-col grow-1 min-w-0">
          <span className="text-xs font-semibold text-bolt-ds-textTertiary truncate min-w-0">Teams plan</span>
          <span className="text-base truncate min-w-0">{activeOrganization?.name}</span>
        </div>
      </div>

      {!!activeOrganization && activeOrganization.billing && (
        <TeamPlanPricingSettingsSection organization={activeOrganization} closeSettingsDialog={closeDialog} />
      )}

      <div className="flex flex-col gap-3.5">
        <h3 className="text-lg font-semibold">Consumption by members</h3>

        <Table className="w-full">
          <TableHead className={gridColumnClassName}>
            <div>Member</div>
            <div>Token balance</div>
          </TableHead>

          <TokenUsageMembersTableContent isLoading={isLoading} members={paginatedMembers} />

          {!isLoading && orgMembers.length > PAGE_SIZE && (
            <Pagination
              page={page}
              totalPages={totalPages}
              onPageChange={onPageChange}
              className="rounded-t-[0px] border-none"
            />
          )}
        </Table>
      </div>
    </SectionContainer>
  );
};
